import { V3PoolToken } from 'types/portfolio/v3'
import { ProcessedV2Position, ProcessedV3Position } from 'types/subgraph'
import { Address } from 'viem'

import { formatNumber } from './formatCurrencyAmount'
import { formatDollarAmount } from './portfolio/formatDollar'

interface V3GroupedPool {
  poolAddress: string
  token0: V3PoolToken
  token1: V3PoolToken
  feeTier: number
  positions: ProcessedV3Position[]
  totalValue: number
}

// Group V3 positions by pool
export function groupV3PositionsByPool(positions: ProcessedV3Position[], tokenPrices?: Record<Address, number>) {
  const grouped = positions.reduce((acc, position) => {
    const poolKey =
      position.sdkPosition.pool.token0.address.toLowerCase() +
      '-' +
      position.sdkPosition.pool.token1.address.toLowerCase() +
      '-' +
      position.sdkPosition.pool.fee

    if (!acc[poolKey]) {
      acc[poolKey] = {
        poolAddress: poolKey,
        token0: {
          id: position.enrichedToken0.id,
          address: position.enrichedToken0.id,
          symbol: position.enrichedToken0.symbol,
          name: position.enrichedToken0.name,
          decimals: parseInt(position.enrichedToken0.decimals),
          logoUri: position.enrichedToken0.logoUri,
        },
        token1: {
          id: position.enrichedToken1.id,
          address: position.enrichedToken1.id,
          symbol: position.enrichedToken1.symbol,
          name: position.enrichedToken1.name,
          decimals: parseInt(position.enrichedToken1.decimals),
          logoUri: position.enrichedToken1.logoUri,
        },
        feeTier: position.sdkPosition.pool.fee,
        positions: [],
        totalValue: 0,
      }
    }

    acc[poolKey].positions.push(position)

    // Calculate position value with actual prices
    const positionValue = calculateV3PositionValue(position, tokenPrices)
    acc[poolKey].totalValue += positionValue

    return acc
  }, {} as Record<string, V3GroupedPool>)

  return Object.values(grouped)
}

// Calculate V3 position value using actual token prices
export function calculateV3PositionValue(position: ProcessedV3Position, tokenPrices?: Record<Address, number>): number {
  if (!tokenPrices) return 0

  const token0Price = tokenPrices[position.sdkPosition.pool.token0.address.toLowerCase() as Address] || 0
  const token1Price = tokenPrices[position.sdkPosition.pool.token1.address.toLowerCase() as Address] || 0

  if (token0Price === 0 && token1Price === 0) return 0

  // Get token amounts from SDK position
  const token0Amount = parseFloat(position.sdkPosition.amount0.toExact())
  const token1Amount = parseFloat(position.sdkPosition.amount1.toExact())

  // Convert uncollected fees to decimal values
  const fees0Amount =
    parseFloat(position.uncollectedFeesToken0) / Math.pow(10, position.sdkPosition.pool.token0.decimals)
  const fees1Amount =
    parseFloat(position.uncollectedFeesToken1) / Math.pow(10, position.sdkPosition.pool.token1.decimals)

  // Calculate total value (position + uncollected fees)
  const token0Value = (token0Amount + fees0Amount) * token0Price
  const token1Value = (token1Amount + fees1Amount) * token1Price

  return token0Value + token1Value
}

// Calculate V2 position value using actual token prices
export function calculateV2PositionValue(
  position: ProcessedV2Position,
  tokenPrices?: Record<Address, number>
): { token0Value: number; token1Value: number; totalValue: number } {
  // Fallback to calculating from token amounts and prices
  if (!tokenPrices) return { token0Value: 0, token1Value: 0, totalValue: 0 }

  const token0Price = tokenPrices[position.token0.address.toLowerCase() as Address] || 0
  const token1Price = tokenPrices[position.token1.address.toLowerCase() as Address] || 0

  if (token0Price === 0 && token1Price === 0) return { token0Value: 0, token1Value: 0, totalValue: 0 }

  // Token amounts from V2 subgraph reserves are already normalized
  const token0Amount = parseFloat(position.token0Amount)
  const token1Amount = parseFloat(position.token1Amount)

  // Calculate total value
  const token0Value = token0Amount * token0Price
  const token1Value = token1Amount * token1Price

  return { token0Value, token1Value, totalValue: token0Value + token1Value }
}

// Check if V3 position is in range
export function isV3PositionInRange(position: ProcessedV3Position): boolean {
  const currentTick = position.sdkPosition.pool.tickCurrent
  return currentTick >= position.sdkPosition.tickLower && currentTick < position.sdkPosition.tickUpper
}

// Get position age in days
export function getPositionAge(createdTimestamp: string): number {
  const created = new Date(parseInt(createdTimestamp) * 1000)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - created.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

// Calculate USD value for fees
export function calculateFeeUSDValue(
  fee0Amount: string,
  fee1Amount: string,
  position: ProcessedV3Position,
  tokenPrices?: Record<Address, number>
): { fee0USD: number; fee1USD: number; totalFeesUSD: number } {
  if (!tokenPrices) {
    return { fee0USD: 0, fee1USD: 0, totalFeesUSD: 0 }
  }

  const token0Price = tokenPrices[position.sdkPosition.pool.token0.address.toLowerCase() as Address] || 0
  const token1Price = tokenPrices[position.sdkPosition.pool.token1.address.toLowerCase() as Address] || 0

  // Convert fee amounts to decimal values
  const fee0Decimal = parseFloat(fee0Amount) / Math.pow(10, position.sdkPosition.pool.token0.decimals)
  const fee1Decimal = parseFloat(fee1Amount) / Math.pow(10, position.sdkPosition.pool.token1.decimals)

  const fee0USD = fee0Decimal * token0Price
  const fee1USD = fee1Decimal * token1Price
  const totalFeesUSD = fee0USD + fee1USD

  return { fee0USD, fee1USD, totalFeesUSD }
}

// Format token amount with USD value
export function formatTokenAmountWithUSD(amount: string, symbol: string, decimals: number, usdValue: number): string {
  if (!amount || amount === '0') return '0'

  try {
    const num = parseFloat(amount)
    const adjusted = decimals > 0 ? num / Math.pow(10, decimals) : num
    const tokenFormatted = formatNumber(adjusted)
    const usdFormatted = formatDollarAmount(usdValue, 2)

    return `${tokenFormatted} ${symbol} (${usdFormatted})`
  } catch {
    return '0'
  }
}

// Format position token amounts from SDK position (V3 only)
export function formatV3PositionTokenAmounts(
  position: ProcessedV3Position,
  tokenPrices?: Record<Address, number>
): { token0Display: string; token1Display: string } {
  const token0AmountNum = parseFloat(position.sdkPosition.amount0.toExact())
  const token1AmountNum = parseFloat(position.sdkPosition.amount1.toExact())

  const token0Amount = formatNumber(token0AmountNum)
  const token1Amount = formatNumber(token1AmountNum)

  // If we have token prices, include USD values
  if (tokenPrices) {
    const token0Price = tokenPrices[position.enrichedToken0.id.toLowerCase() as Address] || 0
    const token1Price = tokenPrices[position.enrichedToken1.id.toLowerCase() as Address] || 0

    const token0USD = token0AmountNum * token0Price
    const token1USD = token1AmountNum * token1Price

    return {
      token0Display: `${token0Amount} ${position.enrichedToken0.symbol} (${formatDollarAmount(token0USD)})`,
      token1Display: `${token1Amount} ${position.enrichedToken1.symbol} (${formatDollarAmount(token1USD)})`,
    }
  }

  return {
    token0Display: `${token0Amount} ${position.enrichedToken0.symbol}`,
    token1Display: `${token1Amount} ${position.enrichedToken1.symbol}`,
  }
}

// // Format token amounts for display (V2 and legacy use)
// export function formatPositionTokenAmounts(
//   amount0: string,
//   amount1: string,
//   token0Symbol: string,
//   token1Symbol: string,
//   token0Decimals: number = 18,
//   token1Decimals: number = 18
// ): { token0Display: string; token1Display: string } {
//   const formatTokenAmount = (amount: string, decimals: number) => {
//     if (!amount || amount === '0') return '0'

//     try {
//       const num = parseFloat(amount)
//       const adjusted = decimals > 0 ? num / Math.pow(10, decimals) : num
//       return formatAmount(adjusted, 6)
//     } catch {
//       return '0'
//     }
//   }

//   return {
//     token0Display: `${formatTokenAmount(amount0, token0Decimals)} ${token0Symbol}`,
//     token1Display: `${formatTokenAmount(amount1, token1Decimals)} ${token1Symbol}`,
//   }
// }
